import {makeAutoObservable} from "mobx";
import _ from "lodash";

import CsInstance from "./CsInstance";
import getExtensions, {
	CodeMirrorCompartments,
} from "../components/CodeEditor/CodeMirrorExtensions/getExtensions";
import {updateNoneditableViewRegions} from "../components/CodeEditor/CodeMirrorExtensions/snippetsExtension";

import {Compartment, EditorState} from "@codemirror/state";
import {EditorView} from "@codemirror/view";
import {EMPTY_EDITOR_PROMPT} from "./codeStore";

/**
 * The state of a file in the CodeMirror editor: everything we need to recreate
 * that file in CodeMirror.
 *
 * Includes the editor state (which contains file history, content, etc.), as
 * well as the attached compartments for updating extensions.
 */
type FileState = {
	editorState: EditorState;
	compartments: CodeMirrorCompartments;
};

/**
 * Maps files to their most up-to-date state data for edit history across tabs
 * and projects.
 */
type FileStateMap = {[filename: string]: FileState};

/**
 * Maps project names to their files' CodeMirror states.
 */
type ProjectViewStates = {[projectName: string]: FileStateMap};

/**
 * MobX store for CodeMirror integration.  Mainly handles state management, as
 * well as updating the editor view as necessary.
 */
class CodeMirrorStore {
	/**
	 * Fields used to handle editor integration
	 */
	private _codemirrorViewStates: ProjectViewStates = {};
	private _codemirrorCompartments: CodeMirrorCompartments = {
		editableCompartment: new Compartment(),
		fontSizeCompartment: new Compartment(),
		themeCompartment: new Compartment(),
	};
	private _codemirrorView: EditorView | null = null;

	private _csInstance: CsInstance;

	/**
	 * @param props.csInstance The CsInstance for access to other stores.
	 */
	constructor(props: {csInstance: CsInstance}) {
		this._csInstance = props.csInstance;
		makeAutoObservable(this);
	}

	/**
	 * Whenever the editor value changes, the following places need to be updated:
	 * 1. CodeStore.codemirrorViewStates
	 * 2. CodeStore.fileMap
	 * 3. Local storage
	 * 4. CodeStore.hasUncompiledChanges
	 * 5. Cafebabe
	 */
	handleEditorChange(value: string) {
		const {codeStore} = this._csInstance;
		const {focusedFile} = codeStore;

		// If no file is focused, do nothing
		if (focusedFile === null) {
			return;
		}

		// 1. Update codeStore's saved state/history as editor value changes
		const view = this.codemirrorView;
		if (view) {
			// Update codeStore's saved state/history as editor value changes
			const oldState = this.getCodeMirrorViewStateForFocusedFile();
			if (oldState) {
				// If the old state is different from the new state, update the old state
				if (!oldState.editorState.doc.eq(view.state.toText(value))) {
					this.setCodeMirrorViewStateForFocusedFile({
						editorState: view.state,
						compartments: oldState.compartments,
					});
				}
			} else {
				// If no state history exists, something's wrong
				throw new Error("No state history exists for the focused file");
			}

			// Update the editor to grey out non-editable regions
			updateNoneditableViewRegions(view);
		}

		// 2. Update the focused file's data in codeStore
		codeStore.setFileData(focusedFile, {
			name: focusedFile,
			value: value || "",
			attributes: codeStore.fileMap[focusedFile]?.attributes,
		});

		// 3. Save the changes to local storage
		codeStore.saveProjectToLocalStorage();

		// 4. Set the hasUncompiledChanges flag
		codeStore.hasUncompiledChanges = true;

		// 5. Pass these changes to Cafebabe for project scoping
		codeStore.cafebabeProject?.updateFile(focusedFile, value || "");
	}

	/**
	 * Updates the CodeMirror editor content with the given value. This is used
	 * when content should change due to an external event (e.g. a file is
	 * opened/closed). If the editor is not a CodeMirror instance, this function
	 * does nothing.
	 *
	 * Also loads the previous edit history, if any.
	 *
	 * @param value The new editor value.
	 */
	updateEditorContent(value: string) {
		// Monaco updates automatically.  We only need to manually update the editor
		// content if we're using CodeMirror.
		const view = this.codemirrorView;
		if (!view || !this._csInstance.codeStore.focusedFile) {
			return;
		}

		const savedState = this.getCodeMirrorViewStateForFocusedFile();
		if (savedState) {
			// If the focused file has existing edit history/state, apply it to the view
			view.setState(savedState.editorState);
			this.codemirrorCompartments = savedState.compartments;

			// Update the font size since it may have changed
			const fontSizeCompartment =
				this.codemirrorCompartments.fontSizeCompartment;
			const fontSize = this._csInstance.uiStore.editorFontSize;
			const newTheme = EditorView.theme({
				"& .cm-scroller": {
					fontSize: fontSize + "rem",
				},
			});

			// view.dispatch edits in-place, so we don't need to save a new one
			view.dispatch({
				changes: {
					from: 0,
					to: view.state.doc.length,
					insert: value,
				},
				effects: fontSizeCompartment.reconfigure(newTheme),
			});
		} else {
			// Otherwise, we need to create a new state.

			// Create the extensions for the editor
			const compartments: CodeMirrorCompartments = {
				editableCompartment: new Compartment(),
				fontSizeCompartment: new Compartment(),
				themeCompartment: new Compartment(),
			};
			this._codemirrorCompartments = compartments;
			const extensions = getExtensions(this._csInstance, compartments);

			// Save new state to the project's view state map
			const newState = EditorState.create({doc: value, extensions});
			this.setCodeMirrorViewStateForFocusedFile({
				editorState: newState,
				compartments,
			});
			view.setState(newState);
		}
		// Update the editor to grey out non-editable regions
		updateNoneditableViewRegions(view);
	}

	/**
	 * Gets the code mirror view state for the focused file.
	 * @returns The code mirror view state for the focused file, or undefined if
	 * 		the focused file is not found.
	 */
	getCodeMirrorViewStateForFocusedFile(): FileState | undefined {
		const {codeStore} = this._csInstance;
		const projectName: string = codeStore.projectName || "";
		const focusedFileName: string | null = codeStore.focusedFile;
		if (!focusedFileName) {
			return;
		}
		return this.codemirrorViewStates[projectName]?.[focusedFileName];
	}

	/**
	 * Adds a new code mirror view state for the focused file.
	 * @param newState The new state to set.
	 */
	setCodeMirrorViewStateForFocusedFile(newState: FileState) {
		const {codeStore} = this._csInstance;
		const projectName: string = codeStore.projectName || "";
		const focusedFileName: string | null = codeStore.focusedFile;
		if (!focusedFileName) {
			return;
		}
		this.codemirrorViewStates[projectName] = Object.assign(
			this.codemirrorViewStates[projectName] || {},
			{[focusedFileName]: newState}
		);
	}

	/**
	 * This message is intended to be displayed when no files are open.
	 * Disable edits and display the empty editor prompt, which is:
	 * "Please open a file to start editing".
	 */
	showEmptyEditorPrompt() {
		const view = this.codemirrorView;
		if (!view) {
			return;
		}
		const compartments: CodeMirrorCompartments = {
			editableCompartment: new Compartment(),
			fontSizeCompartment: new Compartment(),
			themeCompartment: new Compartment(),
		};
		this._codemirrorCompartments = compartments;
		const extensions = getExtensions(this._csInstance, compartments);
		view.setState(EditorState.create({doc: EMPTY_EDITOR_PROMPT, extensions}));
	}

	/**
	 * Updates the editor content with the value of the focused file.
	 * If the focused file is not found, the empty editor prompt is shown.
	 */
	updateEditorContentFromFocusedFile() {
		const {codeStore} = this._csInstance;
		if (!codeStore.focusedFile) {
			this.showEmptyEditorPrompt();
			return;
		}
		const focusedFileData = codeStore.visibleFileMap[codeStore.focusedFile];
		if (focusedFileData) {
			this.updateEditorContent(focusedFileData.value);
		}
	}

	/**
	 * Updates whether the editor is editable, based on the relevant stores.
	 */
	updateEditability() {
		const {codeStore} = this._csInstance;
		const editable =
			!this._csInstance.userInputStore.isTheaterFocused &&
			codeStore.filesOpen.length > 0;

		// Update vanilla code editor to be (un)editable
		const view = this.codemirrorView;
		if (view) {
			const editableCompartment =
				this.codemirrorCompartments.editableCompartment;
			view.dispatch({
				effects: editableCompartment.reconfigure(
					EditorView.editable.of(editable)
				),
			});
		}
	}

	/**
	 * Sets the font size for the vanilla codemirror editor.
	 * @param value The new font size, in pixels.
	 */
	setEditorFontSize(value: number) {
		const view = EditorView.findFromDOM(document.body);
		if (view) {
			const fontSizeCompartment =
				this.codemirrorCompartments.fontSizeCompartment;
			const newTheme = EditorView.theme({
				"& .cm-scroller": {
					fontSize: value + "rem",
				},
			});
			view.dispatch({
				effects: fontSizeCompartment.reconfigure(newTheme),
			});
			this.setCodeMirrorViewStateForFocusedFile({
				editorState: view.state,
				compartments: this.codemirrorCompartments,
			});
		}
	}

	// getters and setters for MobX actions

	get codemirrorViewStates() {
		return this._codemirrorViewStates;
	}

	set codemirrorViewStates(value) {
		this._codemirrorViewStates = value;
	}

	get codemirrorCompartments() {
		return this._codemirrorCompartments;
	}

	set codemirrorCompartments(value) {
		this._codemirrorCompartments = value;
	}

	get codemirrorView() {
		return this._codemirrorView;
	}

	set codemirrorView(value) {
		this._codemirrorView = value;
	}
}

export default CodeMirrorStore;
